import { useState } from "react";

import {
  useClearOrphanDrafts,
  useOrphanDrafts,
} from "@/api/orphan-drafts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * JobDetail recovery affordance for terminal jobs whose `tools_draft`
 * rows were never cleared (HITL deadline expired, post-rollup
 * warn-log, or curator failed mid-author). Renders nothing on jobs
 * that have no orphan rows — the orchestrator's `canClear` predicate
 * gates visibility.
 *
 * Click opens a confirmation modal listing the slugs about to be
 * cleared. Confirm POSTs to `/api/jobs/:id/clear-orphan-drafts`. On
 * success the orphan-drafts query invalidates, the next GET returns
 * `canClear: false`, and the banner unmounts.
 *
 * See `apps/admin-ui/src/api/orphan-drafts.ts` and
 * `services/orchestrator/src/routes/clear-orphan-drafts.ts` for the
 * full design. Filed for issue #158.
 */
export interface ClearOrphanDraftsBannerProps {
  jobId: string;
}

export function ClearOrphanDraftsBanner({ jobId }: ClearOrphanDraftsBannerProps) {
  const probe = useOrphanDrafts(jobId);
  const clear = useClearOrphanDrafts();
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Banner renders only when the orchestrator's `canClear` predicate
  // holds — terminal status AND non-empty intersection. Pending/errored
  // probes render nothing so a transient API failure doesn't surface
  // a spurious banner.
  if (probe.data?.canClear !== true) return null;

  const slugs = probe.data.orphanSlugs;
  const slugCount = slugs.length;
  const handleClear = () => {
    clear.mutate(jobId, {
      onSuccess: () => setConfirmOpen(false),
    });
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 rounded border border-amber-400/40 bg-amber-400/10 px-4 py-3">
        <div>
          <p className="text-sm text-text">
            {slugCount} orphan {slugCount === 1 ? "draft row" : "draft rows"}{" "}
            in <span className="font-mono">tools_draft</span> from this job.
          </p>
          <p className="text-xs text-text-muted break-all">
            Clear them to unblock the next curator dispatch.
          </p>
          {clear.isError ? (
            <p className="text-xs text-red-300 font-mono break-all">
              Clear failed:{" "}
              {clear.error instanceof Error
                ? clear.error.message
                : String(clear.error)}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          disabled={clear.isPending}
          onClick={() => setConfirmOpen(true)}
          className="inline-flex h-9 items-center justify-center rounded bg-amber-400 px-4 text-sm font-medium text-bg hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {clear.isPending ? "Clearing…" : "Clear drafts"}
        </button>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear {slugCount} orphan draft {slugCount === 1 ? "row" : "rows"}?</DialogTitle>
            <DialogDescription>
              The rows below will be deleted from{" "}
              <span className="font-mono">tools_draft</span>. The
              underlying authorship trail is preserved.
            </DialogDescription>
          </DialogHeader>
          <ul className="max-h-40 list-disc space-y-1 overflow-y-auto pl-6 text-sm text-text">
            {slugs.map((slug) => (
              <li key={slug}>
                <span className="font-mono text-xs text-text-muted">{slug}</span>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmOpen(false)}
              disabled={clear.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleClear}
              disabled={clear.isPending}
            >
              {clear.isPending ? "Clearing…" : "Clear drafts"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
